import { ChartMargin, ChartLegendConfig, ChartAxisConfig, BlockSettings } from '../types';

export const DEFAULT_CHART_MARGINS: ChartMargin = {
    top: 40,
    right: 80,
    bottom: 80,
    left: 80,
};

export const LINE_CHART_MARGINS: ChartMargin = {
    top: 40,
    right: 110,
    bottom: 70,
    left: 60,
};

export const BAR_CHART_MARGINS: ChartMargin = {
    top: 0,
    right: 130,
    bottom: 70,
    left: 60,
};

export const PIE_LEGEND_CONFIG: ChartLegendConfig = {
    anchor: 'top-left',
    direction: 'column',
    translateY: 0,
    translateX: -50,
    justify: false,
    itemWidth: 100,
    itemHeight: 18,
    itemTextColor: '#000',
    itemDirection: 'left-to-right',
    symbolSize: 15,
    symbolShape: 'circle',
    effects: [
        {
            on: 'hover',
            style: {
                itemTextColor: '#000',
            },
        },
    ],
};

export const PIE_LEGEND_BOTTOM_CONFIG: ChartLegendConfig = {
    anchor: 'bottom',
    direction: 'row',
    translateY: 56,
    justify: false,
    itemWidth: 120,
    itemHeight: 18,
    itemsSpacing: 100,
    itemTextColor: '#999',
    itemDirection: 'left-to-right',
    symbolSize: 18,
    symbolShape: 'circle',
    effects: [
        {
            on: 'hover',
            style: {
                itemTextColor: '#000',
            },
        },
    ],
};

export const BAR_LEGEND_CONFIG: ChartLegendConfig = {
    anchor: 'right',
    direction: 'column',
    justify: false,
    translateX: 120,
    translateY: 50,
    itemsSpacing: 2,
    itemWidth: 80,
    itemHeight: 20,
    itemDirection: 'left-to-right',
    itemOpacity: 0.85,
    symbolSize: 20,
    symbolShape: 'circle',
    effects: [
        {
            on: 'hover',
            style: {
                itemOpacity: 1,
            },
        },
    ],
};

export const LINE_LEGEND_CONFIG: ChartLegendConfig = {
    anchor: 'bottom-right',
    direction: 'column',
    justify: false,
    translateX: 100,
    translateY: 0,
    itemsSpacing: 0,
    itemDirection: 'left-to-right',
    itemWidth: 80,
    itemHeight: 20,
    itemOpacity: 0.75,
    symbolSize: 12,
    symbolShape: 'circle',
    symbolBorderColor: 'rgba(0, 0, 0, .5)',
    effects: [
        {
            on: 'hover',
            style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
            },
        },
    ],
};

export const createAxisConfig = (
    legend: string,
    position: 'bottom' | 'left',
    isIntegerFormat = false
): ChartAxisConfig => {
    const baseConfig: ChartAxisConfig = {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend,
        legendPosition: 'middle',
        legendOffset: position === 'bottom' ? 32 : -40,
    };

    if (isIntegerFormat) {
        baseConfig.format = (e: number) => (Math.floor(e) === e ? e : false);
    }

    return baseConfig;
};

export const CHART_THEME = {
    axis: {
        legend: {
            text: {
                fontWeight: 700,
            },
        },
        ticks: {
            text: {
                fontWeight: 300,
            },
        },
    },
};

export const PIE_DEFS = [
    {
        id: 'dots',
        type: 'patternDots',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.3)',
        size: 4,
        padding: 1,
        stagger: true,
    },
    {
        id: 'lines',
        type: 'patternLines',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.3)',
        rotation: -45,
        lineWidth: 6,
        spacing: 10,
    },
];

export const COLOR_SCHEMES = [
    'nivo',
    'blues',
    'greens',
    'oranges',
    'purples',
    'reds',
    'greys',
    'category10',
    'accent',
    'dark2',
    'paired',
    'pastel1',
    'pastel2',
    'set1',
    'set2',
    'set3',
    'spectral',
] as const;

export type ColorScheme = (typeof COLOR_SCHEMES)[number];

export const getGroupMode = (settings: BlockSettings): 'stacked' | 'grouped' => {
    return settings.group_format === 'stacked' ? 'stacked' : 'grouped';
};

export const getChartLayout = (settings: BlockSettings): 'horizontal' | 'vertical' => {
    return settings.chart_layout === 'horizontal' ? 'horizontal' : 'vertical';
};

export const isReverseAxis = (settings: BlockSettings): boolean => {
    return settings.reverse_axis === 'yes';
};

export const isLabelsEnabled = (settings: BlockSettings): boolean => {
    return settings.enable_labels === 'yes';
};

export const isGridEnabled = (settings: BlockSettings): boolean => {
    return settings.enable_grid === 'yes';
};

export const getSchemeOrDefault = (scheme: string | null | undefined): string => {
    return scheme ?? 'nivo';
};

export const getBarChartColorFunction = (
    scheme: string | null | undefined,
    hasColors: boolean
): Record<string, string> | ((params: { id: string; data: Record<string, string> }) => string) => {
    if (scheme) {
        return { scheme };
    }

    if (hasColors) {
        return ({ id, data }) => String(data[`${id}Color`]);
    }

    return { scheme: 'nivo' };
};
